/**
 * publishReadiness — the client-side MIRROR of the server's publish readiness
 * checks (BON-215). Pure + framework-free so the modal can gate its Confirm
 * button with fast feedback while the server stays authoritative (a race that
 * slips past this mirror is caught by the endpoint's 409 + fieldErrors).
 *
 * Blockers mirror the server's `fieldErrors` exactly. Warnings are client-only
 * (BON-469) — the server does not enforce the lead time.
 *
 * The blocking rules mirror the frozen contract exactly:
 *   - type assigned           → key `workshopTypeId`(everyone)
 *   - start < end             → key `endsAt`        (everyone)
 *   - start not in the past   → key `startsAt`      (everyone, incl. admins)
 *
 * The lead-time threshold is NEVER hardcoded — it reuses MIN_WORKSHOP_LEAD_DAYS
 * from the create flow (single source, mirrors the server constant).
 */
import type { WorkshopDetail } from '../../../../api/workshops';
import { MIN_WORKSHOP_LEAD_DAYS, MIN_LEAD_MESSAGE } from '../create/workshopLeadTime';

/** One failed readiness check. `field` matches the server's `fieldErrors` key so
 *  the modal can reconcile client blockers with a server 409 by the same key. */
export interface ReadinessBlocker {
  field: 'workshopTypeId' | 'endsAt' | 'startsAt';
  message: string;
}

export interface ReadinessResult {
  ready: boolean;
  blockers: ReadinessBlocker[];
  /** Non-blocking notices (BON-469). Shown in the modal so the operator
   *  publishes knowingly; Confirm stays enabled. */
  warnings: string[];
}

export interface ReadinessInput {
  workshop: WorkshopDetail;
  /** Injectable clock for deterministic tests; defaults to now. */
  now?: Date;
}

/**
 * Compute the client-side readiness of a draft workshop. Returns EVERY
 * applicable blocker (not just the first) so the modal can list them all —
 * matching the server, which returns all `fieldErrors` at once.
 */
export function computeReadiness({ workshop, now = new Date() }: ReadinessInput): ReadinessResult {
  const blockers: ReadinessBlocker[] = [];
  const warnings: string[] = [];
  const start = new Date(workshop.startsAt);
  const end = new Date(workshop.endsAt);

  if (!workshop.workshopTypeId) {
    blockers.push({
      field: 'workshopTypeId',
      message: 'Assign a workshop type before publishing.',
    });
  }
  if (!(start.getTime() < end.getTime())) {
    blockers.push({ field: 'endsAt', message: 'The end time must be after the start time.' });
  }

  // A past start still BLOCKS everyone. The 6-week lead only WARNS (BON-469):
  // it is a scheduling nudge, not an invariant, so the operator is told and
  // decides. A past date skips the lead notice — it is already inside the
  // window and the blocker says the more important thing.
  if (start.getTime() < now.getTime()) {
    blockers.push({ field: 'startsAt', message: 'The start date is in the past.' });
  } else if (start.getTime() < now.getTime() + MIN_WORKSHOP_LEAD_DAYS * 86_400_000) {
    warnings.push(MIN_LEAD_MESSAGE);
  }

  return { ready: blockers.length === 0, blockers, warnings };
}
